import { useEffect, useState } from 'react';
import { getUserEmail } from '../auth';
import { emitDevReply, getDevPrompt } from '../api/planningAssessments';
import { errMessage, runEvent } from '../pageUtils';
import { LOST_CONNECTION, STILL_PROCESSING } from '../api/events';

// Dev mode: the run stopped before the model. Shows exactly what the model would have been sent, to paste into a Claude chat
// using the same model, and takes Claude's reply back; the reply is then scored and reported like a model's own.

/** "eu.anthropic.claude-sonnet-4-6" → "Claude Sonnet 4.6"; anything else as it is. */
export function modelLabel(id: string | null | undefined): string {
  const m = /claude-(opus|sonnet|haiku)-(\d+)-(\d+)/i.exec(id ?? '');
  return m ? `Claude ${m[1][0].toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}.${m[3]}` : (id || 'the configured model');
}

export function DevReplyPanel({ caseId, assessmentId, model, onSubmitted }: {
  caseId: string; assessmentId: string; model: string | null; onSubmitted: () => void;
}) {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState<'reply' | 'abandon' | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setPromptError(null);
    getDevPrompt(assessmentId)
      .then(p => { if (!cancelled) setPrompt(p); })
      .catch((e: Error) => { if (!cancelled) setPromptError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assessmentId]);

  async function copy() {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setPromptError('Copying was blocked by the browser. Select the text below and copy it instead.');
    }
  }

  function download() {
    if (!prompt) return;
    const url = URL.createObjectURL(new Blob([prompt], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `assessment-prompt-${assessmentId.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function send(abandon: boolean) {
    if (abandon && !window.confirm('Abandon this run? Its token is returned and the case shows as failed; start a new check to try again.')) return;
    setSending(abandon ? 'abandon' : 'reply'); setSendError(null);
    try {
      await runEvent(connId => emitDevReply(caseId, assessmentId, abandon ? null : reply.trim(), getUserEmail() ?? undefined, connId));
      onSubmitted();
    } catch (e) {
      // Only the confirmation was lost (it ran out of time, or the connection dropped): the reply has almost certainly been
      // taken, and the page keeps checking the case, so carry on rather than report a failure.
      const message = e instanceof Error ? e.message : '';
      if (message === STILL_PROCESSING || message === LOST_CONNECTION) { onSubmitted(); return; }
      setSendError(errMessage(e, abandon ? 'The run could not be abandoned.' : 'The reply could not be scored.'));
    } finally {
      setSending(null);
    }
  }

  const label = modelLabel(model);
  const tokens = prompt ? Math.round(prompt.length / 4) : 0;

  return (
    <div className="sx-form">
      <div>
        <p className="sx-step-of">Dev mode</p>
        <h2 className="sx-h2">Answer this in a Claude chat</h2>
        <p className="sx-note">
          Nothing was sent to the model. Paste the text below into a new chat with <strong>{label}</strong>, then paste Claude's whole
          reply back here. It is scored and reported exactly as the model's own answer would be.
        </p>
      </div>

      <div className="sx-field">
        <span className="sx-label">What the model would be sent</span>
        {promptError ? (
          <div className="sx-err">Couldn't load the prompt: {promptError}</div>
        ) : loading ? (
          <span className="skel" style={{ width: '100%', height: 160 }} />
        ) : prompt == null ? (
          <div className="sx-err">The prompt is no longer held (it is kept for two days). Abandon this run and start a new check.</div>
        ) : (
          <>
            <span className="sx-note">{prompt.length.toLocaleString()} characters, about {tokens.toLocaleString()} tokens.</span>
            <textarea className="sx-input sx-textarea dev-prompt" rows={10} readOnly value={prompt} onFocus={e => e.currentTarget.select()} aria-label="Prompt to paste into Claude" />
            <div className="dev-actions">
              <button type="button" className="sx-cta" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy to clipboard'}</button>
              <button type="button" className="sx-cta sx-cta-2" onClick={download}>Download as a text file</button>
            </div>
          </>
        )}
      </div>

      <div className="sx-field">
        <label className="sx-label" htmlFor="dev-reply">Claude's reply</label>
        <textarea
          id="dev-reply" className="sx-input sx-textarea" rows={12} value={reply} disabled={!!sending}
          placeholder="Paste the whole reply, including the factor table and the OVERALL RISK LEVEL line."
          onChange={e => setReply(e.target.value)}
        />
      </div>
      {sendError && <div className="sx-err" role="alert">{sendError}</div>}
      <div className="dev-actions">
        <button type="button" className="sx-cta" disabled={!reply.trim() || !!sending} onClick={() => void send(false)}>
          {sending === 'reply' ? 'Scoring…' : 'Score this reply'}
        </button>
        <button type="button" className="sx-cta sx-cta-2" disabled={!!sending} onClick={() => void send(true)}>
          {sending === 'abandon' ? 'Abandoning…' : 'Abandon this run'}
        </button>
      </div>
    </div>
  );
}
